import { useCallback, useMemo, useState } from "react"
import { z } from "zod"
import { useCrudList } from "../../hooks/useCrud"
import type { PaginatedResult } from "../../hooks/useCrud"

export interface UseDataTableOptions<T> {
  queryKey: string
  url: string
  schema: z.ZodSchema<T>
  defaultSort?: { field: string; dir: "asc" | "desc" }
  defaultPageSize?: number
}

export function useDataTable<T>({
  queryKey,
  url,
  schema,
  defaultSort,
  defaultPageSize = 50,
}: UseDataTableOptions<T>) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [sort, setSort] = useState(defaultSort?.field ?? "")
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultSort?.dir ?? "desc")
  const [filters, setFilters] = useState<Record<string, unknown>>({})

  const apiFilters = useMemo(() => {
    const result: Record<string, unknown> = { page, page_size: pageSize, ...filters }
    if (sort) {
      result.sort = sort
      result.sort_dir = sortDir
    }
    return result
  }, [page, pageSize, sort, sortDir, filters])

  const query = useCrudList<T>(queryKey, url, schema, apiFilters)

  const handleSortChange = useCallback((field: string, dir: "asc" | "desc") => {
    setSort(field)
    setSortDir(dir)
    setPage(1)
  }, [])

  const handlePageChange = useCallback((p: number) => {
    setPage(p)
  }, [])

  const updateFilters = useCallback((next: Record<string, unknown>) => {
    setFilters((prev) => ({ ...prev, ...next }))
    setPage(1)
  }, [])

  const resetFilters = useCallback(() => {
    setFilters({})
    setPage(1)
  }, [])

  return {
    data: query.data as PaginatedResult<T> | undefined,
    isLoading: query.isLoading,
    error: query.error,
    page,
    pageSize,
    sort,
    sortDir,
    setPage,
    setPageSize,
    handleSortChange,
    handlePageChange,
    filters,
    updateFilters,
    resetFilters,
  }
}
